/**
 * Analytics Service
 * Central service for managing analytics tracking
 */

import type { AnalyticsProvider } from '../providers/base.provider'

interface AnalyticsContext {
  userId?: string
  contactId?: string
  locationId?: string
  groupSlug?: string
  groupId?: string
  pagePath?: string
  routeName?: string
  email?: string
  appName?: string
  [key: string]: any
}

class AnalyticsService {
  private context: AnalyticsContext = {}
  private providers: Map<string, AnalyticsProvider> = new Map()
  private debug = false

  initialize(debug = false): void {
    this.debug = debug
    if (this.debug) {
      console.log('[Analytics] Service initialized')
    }
  }

  registerProvider(name: string, provider: AnalyticsProvider): void {
    this.providers.set(name, provider)
    if (this.debug) {
      console.log(`[Analytics] Provider registered: ${name}`)
    }
  }

  getProvider(name: string): AnalyticsProvider | undefined {
    return this.providers.get(name)
  }

  setContext(context: Partial<AnalyticsContext>): void {
    this.context = {
      ...this.context,
      ...context,
    }
    if (this.debug) {
      console.log('[Analytics] Context updated:', this.context)
    }
  }

  updateContext(key: string, value: any): void {
    this.context[key] = value
  }

  getContext(): AnalyticsContext {
    return { ...this.context }
  }

  clearContext(): void {
    this.context = {}
  }

  private enrichProperties(properties?: Record<string, any>): Record<string, any> {
    return {
      ...this.context, // All context (static + dynamic)
      ...properties, // Event-specific properties
      timestamp: Date.now(),
    }
  }

  track(event: string, properties?: Record<string, any>): void {
    const enriched = this.enrichProperties(properties)

    if (this.debug) {
      console.log(`[Analytics] Track: ${event}`, enriched)
    }

    // Send to all registered providers
    this.providers.forEach((provider, name) => {
      try {
        if (typeof provider.track === 'function') {
          provider.track(event, enriched)
        }
      } catch (error) {
        console.error(`[Analytics] Provider ${name} error:`, error)
      }
    })
  }

  identify(userId: string, traits?: Record<string, any>): void {
    // Update context with user info
    this.setContext({
      userId,
      ...traits,
    })

    if (this.debug) {
      console.log(`[Analytics] Identify: ${userId}`, traits)
    }

    // Notify all registered providers
    this.providers.forEach((provider, name) => {
      try {
        if (typeof provider.identify === 'function') {
          provider.identify(userId, traits)
        }
      } catch (error) {
        console.error(`[Analytics] Provider ${name} identify error:`, error)
      }
    })
  }
}

export const analyticsService = new AnalyticsService()

